import { GridWithFilters, SecondaryData } from '@strata/react-grid';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { ClaimInsightsDetailPanel } from 'components/treatments/ClaimInsightsDetailPanel';
import {
    enrichClaimDrugNames,
    getClaimDetailTitle,
} from 'components/treatments/claimInsightsFields';
import { buildClaimInsightsGridColumns } from 'components/treatments/claimInsightsGridColumns';
import { useClaimsGridData } from 'components/treatments/useClaimsGridData';
import { enrichClaimSupplierNames } from 'services/claimInsightsHelpers';
import { useGetAllProductServicesQuery, useGetAllSuppliersQuery } from 'store/api/referenceApi';

const initialSortProperties = [{ id: 'adjudicationDate', desc: true }];

const getEmptyMessage = (data) =>
    data?.length ? 'No claims match the selected filters.' : 'No claims found.';

export const TreatmentClaimsTab = ({
    claims = undefined,
    claimsLoading = false,
    treatmentProductServiceOid = undefined,
    treatmentIsBrand = undefined,
}) => {
    const { data: products } = useGetAllProductServicesQuery();
    const { productServiceMap } = products ?? {};
    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};
    const gridClaims = useMemo(() => {
        const rows = (claims ?? []).map((claim) => ({
            ...claim,
            productServiceOid: claim.productServiceOid ?? treatmentProductServiceOid,
        }));
        const withSource = rows.some((claim) => claim.isTestClaim)
            ? rows.map((claim) => ({
                  ...claim,
                  claimSource: claim.claimSource ?? 'Payer',
              }))
            : rows;
        const withNames = enrichClaimDrugNames(withSource, productServiceMap);

        return withNames.map((claim) => enrichClaimSupplierNames(claim, supplierMap));
    }, [claims, productServiceMap, supplierMap, treatmentProductServiceOid]);
    const {
        activeFilters,
        filterCategories,
        filterPaneWidth,
        gridData,
        onFilter,
        sort,
        updateFilterPaneWidth,
        updateGridSort,
    } = useClaimsGridData({
        data: gridClaims,
        initialSortProperties,
    });
    const columns = useMemo(
        () => buildClaimInsightsGridColumns({ claims: gridClaims, treatmentIsBrand }),
        [gridClaims, treatmentIsBrand],
    );
    const renderRowDetail = (claim, collapse) => (
        <ClaimInsightsDetailPanel
            title={getClaimDetailTitle(claim)}
            claim={claim}
            onClose={collapse}
        />
    );

    return (
        <div className="sds_claim-insights-grid">
            <GridWithFilters
                activeFilters={activeFilters}
                columns={columns}
                data={gridData}
                enableSorting
                filterCategories={filterCategories}
                filterPaneRenderWidth={filterPaneWidth}
                isLoading={claimsLoading}
                noResults={<SecondaryData data={getEmptyMessage(gridClaims)} />}
                onFilter={onFilter}
                onFilterPaneResize={updateFilterPaneWidth}
                sortProperties={sort}
                onSort={updateGridSort}
                renderRowDetail={renderRowDetail}
            />
        </div>
    );
};

TreatmentClaimsTab.propTypes = {
    claims: PropTypes.arrayOf(PropTypes.shape({})),
    claimsLoading: PropTypes.bool,
    treatmentProductServiceOid: PropTypes.string,
    treatmentIsBrand: PropTypes.bool,
};
